import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../../data/mockData';
import AccountSidebar from '../../components/AccountSidebar';
import { request } from '../../utils/request';
import { 
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CreditCardIcon,
  BanknotesIcon,
  ShareIcon,
  ClipboardDocumentIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline';

// API Response Types
interface IncomeRecord {
  id: number;
  userId: number;
  type: 'referral' | 'sale' | 'commission' | 'bonus' | 'withdrawal';
  amount: number;
  currency: string;
  source: string;
  description: string;
  status: 'pending' | 'completed' | 'cancelled' | 'refunded';
  processedAt: string | null;
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

interface IncomeSummary {
  totalEarnings: number;
  thisMonthEarnings: number;
  lastMonthEarnings: number;
  monthlyGrowth: number;
  pendingEarnings: number;
  completedEarnings: number;
  availableBalance: number;
  currency: string;
}

interface IncomeStats {
  totalEarnings: number;
  earningsByType: Array<{
    type: string;
    total: number;
  }>;
  monthlyEarnings: Array<{
    month: number;
    year: number;
    amount: number;
  }>;
  recentIncomes: IncomeRecord[];
  period: string;
}

interface IncomeResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    incomes: IncomeRecord[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

interface SummaryResponse {
  success: boolean;
  code: number;
  message: string;
  result: IncomeSummary;
}

interface StatsResponse {
  success: boolean;
  code: number;
  message: string;
  data: IncomeStats;
}

// Legacy interfaces for backward compatibility
interface CommissionRecord {
  id: string;
  orderId: string;
  customerName: string;
  productName: string;
  orderAmount: number;
  commissionRate: number;
  commissionAmount: number;
  status: 'pending' | 'paid' | 'cancelled';
  date: string;
}

interface WithdrawalRecord {
  id: string;
  amount: number;
  method: 'bank' | 'paypal' | 'stripe';
  status: 'pending' | 'completed' | 'failed';
  date: string;
  reference: string;
}

interface ReferralCoupon {
  id: string;
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
  usageCount: number;
  maxUsage: number;
  expiresAt: string;
  isActive: boolean;
}

const Income: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [activeTab, setActiveTab] = useState<'overview' | 'commissions' | 'withdrawals' | 'referrals'>('overview');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalMethod, setWithdrawalMethod] = useState<'bank' | 'paypal' | 'stripe'>('bank');
  
  // API data states
  const [incomeSummary, setIncomeSummary] = useState<IncomeSummary | null>(null);
  const [incomeRecords, setIncomeRecords] = useState<IncomeRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    loadIncomeSummary();
    loadIncomeRecords();
  }, [activeTab]);

  const loadIncomeSummary = async () => {
    try {
      const response = await request({
        method: 'GET',
        url: 'income/summary'
      }) as SummaryResponse;
      console.log('response..........', response);
      if (response.success) {
        setIncomeSummary(response.result);
      } else {
        setError(response.message || 'Failed to load income summary');
      }
    } catch (err) {
      console.error('Error loading income summary:', err);
      setError('Failed to load income summary');
    }
  };

  const loadIncomeRecords = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      
      // Filter by type based on active tab
      if (activeTab === 'commissions') {
        params.append('type', 'commission');
      } else if (activeTab === 'withdrawals') {
        params.append('type', 'withdrawal');
      } else if (activeTab === 'referrals') {
        params.append('type', 'referral');
      }

      const response = await request({
        method: 'GET',
        url: `income?${params.toString()}`
      }) as IncomeResponse;

      if (response.success) {
        setIncomeRecords(response.result.incomes);
        setPagination(response.result.pagination);
      } else {
        setError(response.message || 'Failed to load income records');
      }
    } catch (err) {
      console.error('Error loading income records:', err);
      setError('Failed to load income records');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please log in</h2>
          <Link to="/" className="hover:opacity-80" style={{ color: '#2e3a47' }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  // Use API data or fallback to defaults
  const totalEarnings = incomeSummary?.totalEarnings || 0;
  const availableBalance = incomeSummary?.availableBalance || 0;
  const pendingAmount = incomeSummary?.pendingEarnings || 0;
  const totalWithdrawn = incomeSummary?.completedEarnings || 0;

  // Transform API data to match legacy format
  const commissionRecords: CommissionRecord[] = incomeRecords
    .filter(record => record.type === 'commission')
    .map(record => ({
      id: record.id.toString(),
      orderId: record.source || `ORD-${record.id}`,
      customerName: record.metadata?.customerName || 'Customer ***',
      productName: record.metadata?.productName || record.description || 'Product',
      orderAmount: record.metadata?.orderAmount || record.amount / 0.15, // Estimate order amount
      commissionRate: record.metadata?.commissionRate || 0.15,
      commissionAmount: record.amount,
      status: record.status === 'completed' ? 'paid' : record.status === 'pending' ? 'pending' : 'cancelled',
      date: new Date(record.createdAt).toISOString().split('T')[0]
    }));

  const withdrawalRecords: WithdrawalRecord[] = incomeRecords
    .filter(record => record.type === 'withdrawal')
    .map(record => ({
      id: record.id.toString(),
      amount: record.amount,
      method: record.metadata?.method || 'bank',
      status: record.status === 'completed' ? 'completed' : record.status === 'pending' ? 'pending' : 'failed',
      date: new Date(record.createdAt).toISOString().split('T')[0],
      reference: record.source || `WD-${record.id}`
    }));

 

  const handleWithdrawal = async () => {
    if (withdrawalAmount && parseFloat(withdrawalAmount) > 0) {
      try {
        const response: any = await request({
          method: 'POST',
          url: 'income/withdraw',
          data: {
            amount: parseFloat(withdrawalAmount),
            method: withdrawalMethod
          }
        });

        if (response.success) {
          // Refresh data
          loadIncomeSummary();
          loadIncomeRecords();
          setWithdrawalAmount('');
          // Optional: Add success notification here
          alert('Withdrawal request submitted successfully');
        } else {
          console.error('Withdrawal request error:', response);
          alert(response.message || 'Failed to submit withdrawal request');
        }
      } catch (error) {
        console.error('Withdrawal request error:', error);
        alert('Failed to submit withdrawal request');
      }
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Show success message
  };

  const generateQRCode = (code: string) => {
    // Generate QR code for sharing
    console.log('Generate QR for:', code);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Income & Earnings</h1>
          <p className="text-gray-600">Track your earnings, commissions, and manage withdrawals</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <AccountSidebar currentPage="income" />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: '#2e3a47' }}></div>
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          setError(null);
                          loadIncomeSummary();
                          loadIncomeRecords();
                        }}
                        className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Earnings</p>
              <p className="text-2xl font-bold text-gray-900">${totalEarnings.toFixed(2)}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Available Balance</p>
              <p className="text-2xl font-bold" style={{ color: '#2e3a47' }}>${availableBalance.toFixed(2)}</p>
            </div>
            <div className="p-3 rounded-full" style={{ backgroundColor: '#f0f2f5' }}>
              <ArrowUpIcon className="w-6 h-6" style={{ color: '#2e3a47' }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Amount</p>
              <p className="text-2xl font-bold text-yellow-600">${Number(pendingAmount).toFixed(2)}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <ArrowDownIcon className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Withdrawn</p>
              <p className="text-2xl font-bold text-gray-900">${totalWithdrawn.toFixed(2)}</p>
            </div>
            <div className="bg-gray-100 p-3 rounded-full">
              <BanknotesIcon className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview' },
              { id: 'commissions', name: 'Commission Records' },
              { id: 'withdrawals', name: 'Withdrawals' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={activeTab === tab.id ? { borderBottomColor: '#68aeb0', color: '#68aeb0' } : {}}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Commissions */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Commissions</h3>
                  <div className="space-y-3">
                    {commissionRecords.length > 0 ? (
                      commissionRecords.slice(0, 3).map((record) => (
                        <div key={record.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{record.customerName}</p>
                            <p className="text-sm text-gray-600">{record.productName}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-green-600">+${Number(record.commissionAmount).toFixed(2)}</p>
                            <p className="text-xs text-gray-500">{record.date}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>No commission records found</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Withdrawal */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Withdrawal</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        value={withdrawalAmount}
                        onChange={(e) => setWithdrawalAmount(e.target.value)}
                        placeholder="Enter amount"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                        onFocus={(e) => e.currentTarget.style.borderColor = '#2e3a47'}
                        onBlur={(e) => e.currentTarget.style.borderColor = ''}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Method</label>
                      <select
                        value={withdrawalMethod}
                        onChange={(e) => setWithdrawalMethod(e.target.value as any)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                        onFocus={(e) => e.currentTarget.style.borderColor = '#2e3a47'}
                        onBlur={(e) => e.currentTarget.style.borderColor = ''}
                      >
                        <option value="bank">Bank Transfer</option>
                        <option value="paypal">PayPal</option>
                        <option value="stripe">Stripe</option>
                      </select>
                    </div>
                    <button
                      onClick={handleWithdrawal}
                      disabled={!withdrawalAmount || parseFloat(withdrawalAmount) <= 0}
                      className="w-full text-white py-2 px-4 rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#2e3a47' }}
                      onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#3a4a57' }}
                      onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#2e3a47' }}
                    >
                      Request Withdrawal
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Commission Records Tab */}
          {activeTab === 'commissions' && (
            <div>
              {commissionRecords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {commissionRecords.map((record) => (
                        <tr key={record.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.orderId}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.customerName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.productName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${record.orderAmount.toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">+${Number(record.commissionAmount).toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              record.status === 'paid' ? 'bg-green-100 text-green-800' :
                              record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {record.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <CurrencyDollarIcon className="w-12 h-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No commission records</h3>
                  <p className="text-gray-500">You haven't earned any commissions yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Withdrawals Tab */}
          {activeTab === 'withdrawals' && (
            <div>
              {withdrawalRecords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {withdrawalRecords.map((record) => (
                        <tr key={record.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.reference}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${Number(record.amount).toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{record.method}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              record.status === 'completed' ? 'bg-green-100 text-green-800' :
                              record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {record.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <BanknotesIcon className="w-12 h-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No withdrawal records</h3>
                  <p className="text-gray-500">You haven't made any withdrawals yet.</p>
                </div>
              )}
            </div>
          )}
 
        </div>
      </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Income; 